import { Share2 } from "lucide-react";
import { useEffect, useState } from "react";
import ShareModal from "../pages/ShareModal.tsx";

export function BlogBadge({ label }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700">
      <span className="h-1.5 w-1.5 rounded-full bg-violet-500" />
      {label}
    </span>
  );
}
export function GalleryRenderer({ items }) {
  return (
    <div className="columns-2 md:columns-3 gap-4 space-y-4">
      {items.map(img => (
        <img
          key={img.id}
          src={img.url}
          className="w-full rounded-3xl border border-slate-200 break-inside-avoid"
          alt={img.id}
        />
      ))}
    </div>
  );
}
export function BlockRenderer({ blocks }) {
  return (
    <div className="space-y-6">
      {blocks.map((b) => {
        if (b.type === "heading") {
          return (
            <div key={b.id} className="text-2xl font-semibold text-slate-900">
              {b.text}
            </div>
          );
        }
        if (b.type === "paragraph") {
          return (
            <div key={b.id} className="text-slate-700 leading-relaxed whitespace-pre-line">
              {b.text}
            </div>
          );
        }
        if (b.type === "quote") {
          return (
            <div key={b.id} className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
              <div className="text-slate-900 font-semibold">“{b.text}”</div>
              {b.by ? <div className="mt-2 text-sm text-slate-500">— {b.by}</div> : null}
            </div>
          );
        }
        if (b.type === "image") {
          if (!b.url) return null;
          return (
            <div key={b.id}>
              <img src={b.url} alt="" className="h-[360px] w-full rounded-3xl border border-slate-200 object-contain" />
              {b.caption ? <div className="mt-2 text-sm text-slate-500">{b.caption}</div> : null}
            </div>
          );
        }
        if (b.type === "video") {
          return (
            <div key={b.id} className="rounded-3xl border border-slate-200 bg-slate-900 p-6 text-white/80">
              <div className="text-sm font-semibold">Video</div>
              <div className="mt-2 text-sm break-all">{b.url || "(ingen URL)"}</div>
              {b.caption ? <div className="mt-2 text-sm text-white/60">{b.caption}</div> : null}
            </div>
          );
        }
        if (b.type === "galleri") {
          if (!b.items.length) return null;

          return <GalleryRenderer key={b.id} items={b.items} />;
        }
        return null;
      })}
    </div>
  );
}
export function ArticleModal({ post, onClose, from }) {
  const [openShare, setOpenShare] = useState(false);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  try {
    post.blocks =
      typeof post.blocks === "string"
        ? JSON.parse(post.blocks)
        : post.blocks ?? null;
  } catch {
    post.blocks = null;
  }

  return (
    <div
      className="fixed inset-0 z-50"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" />

      <div className="relative mx-auto h-[92vh] max-w-5xl px-4 pt-6">
        <div className="h-full overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_20px_80px_rgba(15,23,42,0.35)]">
          <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-6 py-4">
            <div className="flex items-center gap-3">
              <BlogBadge label={post.template === "Standard" ? "Innlegg" : post.template} />
              <div className="text-xs text-slate-500">{post.readTimeMin} min</div>
            </div>
            <div className="flex items-center gap-2">
              {(from === "blogs" || from === "blogdetail") && (
                <button onClick={() => setOpenShare(true)} className="inline-flex items-center gap-2 justify-center rounded-full px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:opacity-50 disabled:cursor-not-allowed border border-slate-200 bg-white text-slate-700 hover:bg-slate-50">
                  <Share2 size={18} /> Del
                </button>
              )}
              <div className="hidden sm:block text-xs text-slate-500">ESC for å lukke</div>
              <button className="inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:opacity-50 disabled:cursor-not-allowed border border-slate-200 bg-white text-slate-700 hover:bg-slate-50" onClick={onClose}>
                Lukk
              </button>
            </div>
          </div>

          <div className="h-[calc(92vh-64px)] overflow-auto">
            {post.imageUrl ? <img src={post.imageUrl} alt="" className="h-[340px] w-full object-contain" /> : null}

            <div className="px-6 py-8 sm:px-10">
              <div className="text-3xl font-semibold text-slate-900">{post.title}</div>
              <div className="mt-3 text-slate-600">{post.subtitle}</div>

              <div className="mt-8">
                {post.blocks && post.blocks.length ? (
                  <BlockRenderer blocks={post.blocks} />
                ) : (
                  <div className="rounded-3xl border border-slate-200 bg-slate-50 p-6 text-slate-700">
                    Dette innlegget har ikke blokk-data i demoen. I produksjon kommer blocks fra Teletorget CMS API.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {openShare ? <ShareModal open={openShare} url={window.location.origin+`/blogs?blog=${post.id}`} profileName={"Chamcore"} onClose={() => setOpenShare(false)} type={"bloggpost"} /> : null}
    </div>
  );
}